import { motion } from "framer-motion";
import { Phone, MessageCircle, ShieldCheck } from "lucide-react";
import { BRAND, telHref, waHref, LOGO_URL } from "@/lib/brand";

export default function CTA() {
    return (
        <section
            id="contact"
            data-testid="cta-section"
            className="relative py-24 lg:py-32 bg-white overflow-hidden"
        >
            {/* subtle background */}
            <div className="absolute inset-0">
                <img
                    src="https://images.pexels.com/photos/9667643/pexels-photo-9667643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
                    alt=""
                    className="w-full h-full object-cover opacity-[0.07]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-white" />
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="text-xs uppercase tracking-[0.3em] text-[#1E88E5] font-bold mb-4">
                            / Get A Free Quote Today
                        </div>
                        <h2 className="font-display font-bold uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-[#0a0a0a]">
                            Ready for a{" "}
                            <span className="text-[#1E88E5]">stronger</span>{" "}
                            clean?
                        </h2>
                        <p className="mt-6 text-gray-600 text-lg max-w-md">
                            Call Jefferson directly. Free estimates, honest
                            pricing, and a satisfaction guarantee on every job.
                        </p>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <a
                                href={telHref}
                                data-testid="cta-call-btn"
                                className="inline-flex items-center justify-center gap-3 bg-[#1E88E5] hover:bg-[#1976D2] text-white font-bold uppercase tracking-wider px-7 py-4 rounded-sm transition-all"
                            >
                                <Phone className="w-5 h-5" />
                                {BRAND.phoneDisplay}
                            </a>
                            <a
                                href={waHref}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="cta-whatsapp-btn"
                                className="inline-flex items-center justify-center gap-3 bg-green-600 hover:bg-green-500 text-white font-bold uppercase tracking-wider px-7 py-4 rounded-sm transition-all"
                            >
                                <MessageCircle className="w-5 h-5" />
                                WhatsApp
                            </a>
                        </div>

                        <div className="mt-8 flex items-center gap-2 text-sm text-gray-500">
                            <ShieldCheck className="w-4 h-4 text-[#1E88E5]" />
                            Safe Hands · Peace of Mind · Same-day response
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative"
                    >
                        <div className="border border-black/10 bg-white shadow-xl p-8 rounded-sm">
                            <div className="flex items-start gap-4">
                                <img
                                    src={LOGO_URL}
                                    alt="Stronger Solutions"
                                    className="w-16 h-16 object-contain rounded-sm bg-[#0a0a0a] p-1"
                                />
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-[#1E88E5] font-bold">
                                        Owner & Operator
                                    </div>
                                    <div className="font-display font-bold text-3xl mt-1 text-[#0a0a0a]">
                                        Jefferson
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Stronger Solutions · Georgia
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 grid grid-cols-2 gap-px bg-black/10">
                                <a
                                    href={telHref}
                                    data-testid="cta-card-call"
                                    className="bg-white hover:bg-[#1E88E5] transition-colors p-6 group"
                                >
                                    <Phone className="w-5 h-5 text-[#1E88E5] group-hover:text-white mb-3" />
                                    <div className="text-[10px] uppercase tracking-widest text-gray-500 group-hover:text-white/90 font-semibold">
                                        Call Now
                                    </div>
                                    <div className="font-display font-bold text-lg mt-1 text-[#0a0a0a] group-hover:text-white">
                                        {BRAND.phoneDisplay}
                                    </div>
                                </a>
                                <a
                                    href={waHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="cta-card-whatsapp"
                                    className="bg-white hover:bg-green-600 transition-colors p-6 group"
                                >
                                    <MessageCircle className="w-5 h-5 text-green-600 group-hover:text-white mb-3" />
                                    <div className="text-[10px] uppercase tracking-widest text-gray-500 group-hover:text-white/90 font-semibold">
                                        Text / WhatsApp
                                    </div>
                                    <div className="font-display font-bold text-lg mt-1 text-[#0a0a0a] group-hover:text-white">
                                        Chat with us
                                    </div>
                                </a>
                            </div>

                            <div className="mt-6 pt-6 border-t border-black/10 text-xs text-gray-500 italic font-display">
                                "Stronger Solutions For A Cleaner Tomorrow."
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
